import React, { useMemo } from 'react';
import Select, { Props, GroupBase } from 'react-select';
import { ClearIndicator } from './components/ClearIndicator';
import { DropdownIndicator } from './components/DropdownIndicator';
import { SearchIndicator } from './components/SearchIndicator';
import { CustomOption } from './components/CustomOption';
import { MultiValueRemove } from './components/MultiValueRemove';
import { DefaultNoOptionsMessage, NoOptionsMessage } from './components/NoOptionsMessage';
import { getStyles } from './styles';

type IconType = 'arrow' | 'search';

const getDropdownIndicator = (icon?: IconType) => {
    switch (icon) {
        case 'search':
            return SearchIndicator;
        case 'arrow':
        default:
            return DropdownIndicator;
    }
};

export type SearchableSelectProps<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
> = Exclude<Props<Option, IsMulti, Group>, 'styles'> & {
    icon?: IconType;
    height?: string;
    fontSize?: string;
    optionHeight?: string;
    dropDownIndicatorWidth?: string;
    closeIndicatorWidth?: string;
    isInvalid?: boolean;
    minHeight?: string;
};

/**
 * Wrapper around react-select. For docs see https://react-select.com/home
 *
 * Default object type for options is `{ value: string; label: string; }`
 *
 * If using objects with a different shape you should also set the props for `getOptionValue` and `getOptionLabel`
 *
 * Known issues - If using `isMulti=true` you must use the default object type (`{ value: string; label: string; }`) otherwise it always shows no options after the first selection
 */
export const SearchableSelect = <
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>({
    components,
    icon,
    height,
    fontSize,
    optionHeight,
    dropDownIndicatorWidth,
    closeIndicatorWidth,
    minHeight,
    isInvalid,
    ...props
}: SearchableSelectProps<Option, IsMulti, Group>) => {
    const styles = useMemo(
        () =>
            getStyles<Option, IsMulti, Group>(
                height,
                fontSize,
                optionHeight,
                dropDownIndicatorWidth,
                closeIndicatorWidth,
                isInvalid,
                minHeight
            ),
        [height, fontSize, optionHeight, dropDownIndicatorWidth, closeIndicatorWidth, isInvalid, minHeight]
    );

    return (
        <Select
            {...props}
            styles={styles}
            components={{
                ClearIndicator,
                MultiValueRemove,
                DropdownIndicator: getDropdownIndicator(icon),
                NoOptionsMessage: DefaultNoOptionsMessage,
                Option: CustomOption,
                ...components,
            }}
        />
    );
};
SearchableSelect.defaultProps = {
    isSearchable: true,
    isClearable: true,
    icon: 'arrow',
    height: 'auto',
    optionHeight: '48px',
    fontSize: '18px',
    minHeight: '48px',
};

SearchableSelect.NoOptionsMessage = NoOptionsMessage;
